import type { Promotion } from '~/lib/offers-by-group';

import { useServerDateStore } from '~/stores/server-date';

export type DateGroupedPromotions<P = Promotion> = {
  startDate?: string;
  endDate?: string;
  promotions?: P[];
};

const parseDate = (dateTimeString: string): Date | undefined => {
  // Split the date and time parts
  const [datePart, timePart] = dateTimeString.split(' ');
  if (!datePart) return undefined;

  // Split the date string by the dot separator
  const [day, month, year] = datePart.split('.');

  // Default to 00:00 if timePart is not provided

  // Split the time part by the colon separator
  const times = timePart?.split(':').map(Number);

  // Create and return a new Date object
  // Note: Months are 0-indexed in the Date constructor (0 is January, 1 is February, etc.)
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(times?.[0] ?? 0),
    Number(times?.[1] ?? 0)
  );
};

const getPromotionStartDate = (startDate: string | Date | undefined): number => {
  // if undefined then startDate shouldn't matter
  // -> 0
  if (!startDate) return 0;
  if (typeof startDate === 'object') return startDate.getTime();
  return parseDate(startDate)?.getTime() || 0;
};
const getPromotionEndDate = (endDate: string | Date | undefined): number => {
  // if undefined then endDate shouldn't matter
  // -> Infinity
  if (!endDate) return Infinity;
  if (typeof endDate === 'object') return endDate.getTime();
  return parseDate(endDate)?.getTime() || Infinity;
};

export const getPromotionsFromGroup = <P = Promotion>(groups: DateGroupedPromotions<P>[]) => {
  const promotions: P[] = [];
  groups.forEach((group) => {
    if (!group.promotions) return;
    group.promotions.forEach((promotion) => {
      promotions.push({
        ...promotion,
        startDate: group.startDate,
        endDate: group.endDate,
      });
    });
  });
  return promotions;
};

export const getActivePromotions = <P = Promotion>(
  promotions: Promotion<P>[],
  dateToday?: number
): Promotion<P>[] => {
  const serverDate = dateToday || useServerDateStore().serverDateTimestamp;
  return (
    promotions?.filter((promotion) => {
      const startDate = getPromotionStartDate(promotion.startDate);
      const endDate = getPromotionEndDate(promotion.endDate);

      if (startDate > endDate) {
        captureException(new Error('startDate cannot be later then endDate'), {
          captureContext: {
            contexts: {
              promotion,
            },
          },
        });
        return false;
      }

      return startDate < serverDate && endDate > serverDate;
    }) || []
  );
};
